import React, { useState, useEffect, useMemo } from 'react';
import { motion as Motion } from 'framer-motion';
import { Search, X, Shapes, Copy, Check, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { getallspecilitylist, getSpCategoryList, getSpecialtyIconMap } from '../../redux/masterSlice';
import { HEALTH_ICON_MANIFEST } from '../../icons/health';
import HealthIcon from '../../icons/health/HealthIcon';
import { buildIconUsage, useSpecialtyIconStyle, setSpecialtyIconStyle } from '../../specialtyIcons';
import useCanManage from '../../hooks/useCanManage';

// Chip order. Custom is appended only when the generated manifest carries a
// custom icon — an empty chip would just advertise a folder nobody filled.
const BASE_GROUPS = ['all', 'specialties', 'anatomy', 'conditions', 'healthcare'];
const MAX_SEARCH_LENGTH = 50;

// One tile. Module-level so the grid does not redefine it per render; the
// copy button is the only interactive part, so the tile itself is a plain div.
const IconTile = ({ entry, style, usage, onCopy, copied, t }) => {
    const hasSolid = entry.styles.includes('solid');
    const solidMissing = style === 'solid' && !hasSolid;
    const usedBy = usage[entry.key] || [];
    return (
        <div
            className="group relative bg-white border border-gray-200 rounded-lg p-3 flex flex-col items-center text-center transition-all duration-300 hover:border-[#4ABDBD] hover:shadow-lg transform hover:-translate-y-0.5"
            data-testid={`speciality-icons-tile-${entry.key}`}
        >
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            <button
                type="button"
                onClick={() => onCopy(entry.key)}
                className="absolute top-2 right-2 p-1.5 rounded-md text-gray-400 hover:text-[#2D8A8A] hover:bg-[#4ABDBD]/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all cursor-pointer"
                title={t('specialityicons.copy_key', 'Copy key')}
                aria-label={t('specialityicons.copy_key', 'Copy key')}
                data-testid={`speciality-icons-copy-${entry.key}`}
            >
                {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
            </button>
            {/* The 28px preview, in the SAVED platform style. An icon with no
                solid drawing falls back to its outline — the title says so. */}
            <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#4ABDBD]/10 to-[#C7D6D5]/40 text-[#2D8A8A] mb-2"
                title={solidMissing ? t('specialityicons.outline_only', 'No solid drawing yet — this icon stays outline') : entry.label}
                data-testid={`speciality-icons-preview-${entry.key}`}
            >
                <HealthIcon iconKey={entry.key} variant={style} size={28} />
            </div>
            <p className="text-sm font-semibold text-gray-800 line-clamp-1 w-full" title={entry.label} data-testid={`speciality-icons-label-${entry.key}`}>
                {entry.label}
            </p>
            <p className="font-mono text-[11px] text-gray-400 line-clamp-1 w-full" title={entry.key} data-testid={`speciality-icons-key-${entry.key}`}>
                {entry.key}
            </p>
            {solidMissing && (
                <span className="mt-1 text-[10px] uppercase tracking-wide text-amber-600" data-testid={`speciality-icons-outline-only-${entry.key}`}>
                    {t('specialityicons.outline_only_badge', 'Outline only')}
                </span>
            )}
            <p
                className={`mt-2 pt-2 border-t border-gray-100 w-full text-[11px] leading-snug line-clamp-2 ${usedBy.length ? 'text-[#2D8A8A]' : 'text-gray-400'}`}
                title={usedBy.map((u) => u.name).join(', ')}
                data-testid={`speciality-icons-usage-${entry.key}`}
            >
                {usedBy.length
                    ? t('specialityicons.used_by', { names: usedBy.map((u) => u.name).join(', '), defaultValue: 'Used by: {{names}}' })
                    : t('specialityicons.not_used', 'Not assigned yet')}
            </p>
        </div>
    );
};

const SpecialityIcons = () => {
    const { t } = useTranslation('specialityicons');
    const dispatch = useDispatch();
    const { specialties, spCategoryList, specialtyIconMap, specialtyIconMapLoading } = useSelector((state) => state.master);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [group, setGroup] = useState('all');
    const [copiedKey, setCopiedKey] = useState(null);

    // The platform-wide style. It is a SETTING, not a preview: every app draws
    // this style, so the page itself renders every tile in it too. `pending`
    // is the style being saved — shown optimistically, dropped on failure.
    // Two controls, deliberately separate (owner, 2026-09-10). The tab bar
    // only changes what THIS PAGE draws, so a style can be looked at without
    // being imposed on every app; Activate is the one that writes the setting.
    // `preview` null means "follow whatever is live", so after saving — or
    // after another admin changes it — the page snaps back to the truth.
    const canManage = useCanManage('masters.manage');
    const savedStyle = useSpecialtyIconStyle();
    const [previewStyle, setPreviewStyle] = useState(null);
    const [savingStyle, setSavingStyle] = useState(false);
    const style = previewStyle || savedStyle;
    const isLive = style === savedStyle;

    const handleActivate = async () => {
        if (!canManage || savingStyle || isLive) return;
        setSavingStyle(true);
        try {
            await setSpecialtyIconStyle(style);
            setPreviewStyle(null);
            toast.success(t('specialityicons.style_saved', 'Icon style saved — every app will show it shortly.'));
        } catch (err) {
            toast.error(err?.response?.data?.message || t('specialityicons.style_failed', 'Could not save the icon style.'));
        } finally {
            setSavingStyle(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim().toLowerCase()), 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // The usage line needs three open reads: the id → key map, plus the two
    // name lists to turn ids into names. All are cached / cheap.
    useEffect(() => {
        dispatch(getSpecialtyIconMap());
        dispatch(getallspecilitylist({}));
        dispatch(getSpCategoryList());
    }, [dispatch]);

    const hasCustom = useMemo(() => HEALTH_ICON_MANIFEST.some((e) => e.group === 'custom'), []);
    const groups = hasCustom ? [...BASE_GROUPS, 'custom'] : BASE_GROUPS;

    const usage = useMemo(
        () => buildIconUsage(specialtyIconMap, specialties || [], spCategoryList || []),
        [specialtyIconMap, specialties, spCategoryList]
    );

    const visible = useMemo(() => HEALTH_ICON_MANIFEST.filter((e) => {
        if (group !== 'all' && e.group !== group) return false;
        if (!debouncedSearch) return true;
        return e.label.toLowerCase().includes(debouncedSearch) || e.key.includes(debouncedSearch);
    }), [group, debouncedSearch]);

    const counts = useMemo(() => {
        const c = { all: HEALTH_ICON_MANIFEST.length };
        HEALTH_ICON_MANIFEST.forEach((e) => { c[e.group] = (c[e.group] || 0) + 1; });
        return c;
    }, []);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value.replace(/[<>]/g, '').substring(0, MAX_SEARCH_LENGTH));
    };

    const handleCopy = async (key) => {
        try {
            await navigator.clipboard.writeText(key);
            setCopiedKey(key);
            toast.success(t('specialityicons.copied', { key, defaultValue: 'Copied "{{key}}"' }));
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
        } catch {
            toast.warn(t('specialityicons.copy_failed', 'Could not copy — select the key and copy it by hand.'));
        }
    };

    return (
        <div className="w-full" data-testid="speciality-icons-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4" data-testid="speciality-icons-header">
                    <div>
                        <h1 className="text-2xl sm:text-3xl pb-1 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="speciality-icons-title">
                            {t('specialityicons.title', 'Specialty icons')}
                        </h1>
                        <p className="text-sm text-gray-500 ml-2 max-w-3xl flex items-start gap-2" data-testid="speciality-icons-intro">
                            <Info size={16} className="mt-0.5 shrink-0 text-[#4ABDBD]" />
                            <span>
                                {t('specialityicons.intro', 'These icons ship inside the apps: assign one to a specialty or category from its form, and new or redrawn icons arrive with a release (scripts/health-icons/README.md).')}
                            </span>
                        </p>
                    </div>
                    {/* Look at a style here; activate it to send it everywhere. */}
                    {/* A column of right-aligned rows. Without items-end the
                        tab bar would stretch to the width of the help text
                        below it, which is the widest thing in this stack. */}
                    <div className="shrink-0 self-start flex flex-col items-end" data-testid="speciality-icons-style-setting">
                        <div className="flex items-center gap-2 mb-1 justify-end">
                            <span className="text-xs font-semibold text-gray-600" data-testid="speciality-icons-style-label">
                                {t('specialityicons.preview_label', 'Preview style on this page')}
                            </span>
                        </div>
                        <div className="relative inline-flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-xl shadow-sm border border-gray-200" data-testid="speciality-icons-variant-toggle">
                            {['outline', 'solid'].map((v) => {
                                const active = style === v;
                                return (
                                    <button
                                        key={v}
                                        type="button"
                                        onClick={() => setPreviewStyle(v)}
                                        aria-pressed={active}
                                        className={`relative px-4 py-1.5 text-sm rounded-lg transition-colors duration-300 z-10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDBD]/60 ${active ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD]'}`}
                                        data-testid={`speciality-icons-variant-${v}`}
                                    >
                                        {active && (
                                            <Motion.div
                                                layoutId="specialityIconsVariantTab"
                                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-lg shadow !m-0"
                                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                            />
                                        )}
                                        <span className="relative z-20">
                                            {v === 'outline' ? t('specialityicons.variant_outline', 'Outline') : t('specialityicons.variant_solid', 'Solid')}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {/* The live setting. When the preview matches it there is
                            nothing to do, so the row states the fact instead of
                            offering a button that would change nothing. */}
                        <div className="mt-2 flex items-center gap-2 justify-end" data-testid="speciality-icons-live-row">
                            {isLive ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-50 border border-green-200 text-[11px] font-semibold text-green-700" data-testid="speciality-icons-style-live">
                                    <Check size={12} />
                                    {t('specialityicons.live_now', {
                                        style: style === 'solid' ? t('specialityicons.variant_solid', 'Solid') : t('specialityicons.variant_outline', 'Outline'),
                                        defaultValue: 'Live in every app: {{style}}',
                                    })}
                                </span>
                            ) : canManage ? (
                                <button
                                    type="button"
                                    onClick={handleActivate}
                                    disabled={savingStyle}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-xs font-semibold shadow hover:shadow-md transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="speciality-icons-style-activate"
                                >
                                    {savingStyle ? (
                                        <>
                                            <span className="border-2 border-white border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                            {t('specialityicons.style_saving', 'Saving…')}
                                        </>
                                    ) : (
                                        <>
                                            <Check size={13} />
                                            {t('specialityicons.activate', {
                                                style: style === 'solid' ? t('specialityicons.variant_solid', 'Solid') : t('specialityicons.variant_outline', 'Outline'),
                                                defaultValue: 'Use {{style}} in every app',
                                            })}
                                        </>
                                    )}
                                </button>
                            ) : (
                                <span className="text-[11px] text-gray-500" data-testid="speciality-icons-style-readonly">
                                    {t('specialityicons.live_other', {
                                        style: savedStyle === 'solid' ? t('specialityicons.variant_solid', 'Solid') : t('specialityicons.variant_outline', 'Outline'),
                                        defaultValue: 'Live in every app: {{style}}',
                                    })}
                                </span>
                            )}
                        </div>
                        <p className="mt-1.5 text-[11px] leading-snug text-gray-500 max-w-xs text-right ml-auto" data-testid="speciality-icons-style-help">
                            {!canManage
                                ? t('specialityicons.style_help_readonly', 'Every app shows one style. You need the masters permission to change it.')
                                : isLive
                                    ? t('specialityicons.style_help', 'Switch the tabs to see how the other style looks. Nothing changes for the apps until you activate it.')
                                    : t('specialityicons.style_help_pending', 'You are only previewing. The apps still show the live style until you activate this one.')}
                        </p>
                    </div>
                </div>

                {/* Group chips + search */}
                <div className="mb-5 flex flex-col lg:flex-row gap-3 lg:items-center" data-testid="speciality-icons-filter-row">
                    <div className="flex flex-wrap gap-2" data-testid="speciality-icons-group-chips">
                        {groups.map((g) => {
                            const active = group === g;
                            return (
                                <button
                                    key={g}
                                    type="button"
                                    onClick={() => setGroup(g)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer ${
                                        active
                                            ? 'bg-[#4ABDBD] border-[#4ABDBD] text-white shadow-sm'
                                            : 'bg-white border-gray-200 text-gray-600 hover:border-[#4ABDBD] hover:text-[#2D8A8A]'
                                    }`}
                                    data-testid={`speciality-icons-chip-${g}`}
                                >
                                    {t(`specialityicons.group_${g}`, g)}
                                    <span className={`ml-1.5 ${active ? 'text-white/80' : 'text-gray-400'}`}>{counts[g] || 0}</span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="relative group w-full lg:w-1/3 lg:ml-auto" data-testid="speciality-icons-search-container">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={20} />
                        <input
                            type="text"
                            placeholder={t('specialityicons.search_placeholder', 'Search by name or key…')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={MAX_SEARCH_LENGTH}
                            className="w-full pl-12 pr-10 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400"
                            data-testid="speciality-icons-search-input"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:bg-gray-200 transition-colors"
                                data-testid="speciality-icons-search-clear"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Count + usage-loading hint */}
                <div className="flex items-center justify-between mb-3 px-1 text-xs text-gray-500" data-testid="speciality-icons-count-row">
                    <span data-testid="speciality-icons-count">
                        {t('specialityicons.showing_count', { count: visible.length, total: HEALTH_ICON_MANIFEST.length, defaultValue: 'Showing {{count}} of {{total}} icons' })}
                    </span>
                    {specialtyIconMapLoading && (
                        <span className="flex items-center gap-2" data-testid="speciality-icons-usage-loading">
                            <span className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-3.5 h-3.5 animate-spin"></span>
                            {t('specialityicons.loading_usage', 'Loading assignments…')}
                        </span>
                    )}
                </div>

                {/* Grid / empty */}
                {visible.length === 0 ? (
                    <div className="text-center py-16 sm:py-20" data-testid="speciality-icons-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Shapes size={48} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" data-testid="speciality-icons-empty-title">
                            {t('specialityicons.no_icons_found', 'No icons match')}
                        </h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto" data-testid="speciality-icons-empty-text">
                            {t('specialityicons.try_adjusting_search', 'Try another name, key, or group.')}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="speciality-icons-grid">
                        {visible.map((entry) => (
                            <IconTile
                                key={entry.key}
                                entry={entry}
                                style={style}
                                usage={usage}
                                onCopy={handleCopy}
                                copied={copiedKey === entry.key}
                                t={t}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpecialityIcons;
